import {
  Box,
  Flex,
  Image,
  Text,
  Tooltip,
  useOutsideClick,
  useToast,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  VStack,
  Skeleton,
} from '@chakra-ui/react';
import { css } from '@emotion/react';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import ScrollContainer from 'react-indiana-drag-scroll';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import CustomToast from 'components/CustomToast';
import DeleteGroupModal from 'components/DeleteGroupModal';
import Cross from 'components/Icons/Cross';
import InputField from 'components/InputField';
import {
  AddDimnesionGroupItem,
  DeleteGroupDimName,
  DeleteGroupItemName,
  fetchDimensionItemsGroup,
  fetchDimensionPropeties,
  getDataIndicatorsKeys,
  getDimensionDataGroupStatus,
  getGroupItem,
  getIsItemRenamed,
  getItemId,
  getNewItemName,
  getRenameItemName,
  getSelectedElement,
  setDisplayInfo,
  setGroupItem,
  setIsItemRenamed,
  setRenameItemName,
  UpdateDimensionProperty,
  UpdateGroupDimName,
  UpdateGroupItemName,
  UpdateItemName,
} from 'redux/ItemPageSlice';
import {
  closeInputSection,
  fetchBlockDrivers,
  getSelectedDriver,
  setDriver,
} from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import PlanPermission from 'container/PlanPermission';
import FullScreenLoader from 'components/FullScreenLoader';
import { HEADER_HEIGHT } from 'projectConstants';

import Plus from '../../../../components/Icons/Plus';
import { UpdatePropertyValues } from '../../../../models/requestBody/updatePropertyValues.interface';
import apiService from '../../../../services';
import infoIcon from '../Icons/infoIcon.svg';

import AddNewItem from './AddNewItem';
import DesktopElements from './DesktopElements';
import NewGroup from './NewGroup';

interface Properties {
  planPermission: boolean;
  longClickCheck: any;
  isLongClicked: boolean;
  refetchRequest: () => void;
  orgType?: boolean;
}

const styles = {
  container: (open: boolean) => css`
    &:hover {
      cursor: pointer;
      transition: all 0.15s ease-out;

      ${!open && 'background: #9588e7'};
    }

    &::before {
      content: '';
      position: absolute;
      right: 50px;
      width: 30px;
      top: 20px;
    }

    .infoButton {
      ${!open && 'display: none'};
    }

    .infoButtonParent:hover .infoButton {
      display: block;
    }
  `,
};
type AppDispatch = ThunkDispatch<any, any, AnyAction>;

const DesktopPanel = ({
  longClickCheck,
  isLongClicked,
  refetchRequest,
  planPermission,
  orgType,
}: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const { blockId }: any = useParams();
  const reference = React.useRef() as React.MutableRefObject<HTMLInputElement>;
  const queryClient = useQueryClient();
  const currentDriver = useSelector(getSelectedDriver);
  const toast = useToast();
  const baseScenario = useSelector(getBaseScenario);

  useEffect(() => {
    if (currentDriver && currentDriver.type === 'dimension') {
      dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));
      dispatch(fetchDimensionPropeties(currentDriver));
    }
  }, [currentDriver, baseScenario?.id]);

  const [addGroupName, setAddGroupName] = useState<string | null>(null);

  const dataIndicatorsKeys = useSelector(getDataIndicatorsKeys);
  const selectedElement = useSelector(getSelectedElement);
  const groupItem = useSelector(getGroupItem);
  const isLoading = useSelector(getDimensionDataGroupStatus);

  const renameItemName = useSelector(getRenameItemName);
  const isItemRenamed = useSelector(getIsItemRenamed);
  const newItemName = useSelector(getNewItemName);
  const [renameGroup, setRenameGroup]: any = useState(null);
  const [renameGroupValue, setRenameGroupValue] = useState<string>('');
  const [displayGroupInfo, setDisplayGroupInfo]: any = useState(null);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [DeleteIdModal, setDeleteIdModal]: any = useState(null);
  const [DeleteTypeModal, setDeleteTypeModal] = useState('');
  const [DeleteNameModal, setDeleteNameModal] = useState('');
  const [loader, setLoader] = useState(false);
  const itemId = useSelector(getItemId);

  const { mutate: updateValue } = useMutation(
    ({ item_id, request_body }: UpdatePropertyValues) =>
      apiService.update_property_values_group({ item_id, request_body }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['propertyValues', currentDriver?.block_id]);
        dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));
        refetchRequest();
      },
      onError: (error: any) => {
        toast({
          duration: 2500,
          render: () => (
            <CustomToast title={error?.response?.data?.message || 'Error'} status='error' />
          ),
        });
      },
    },
  );

  const handleItem = (name: string): void => {
    if (groupItem === name) {
      dispatch(setGroupItem(null));
      return;
    }
    dispatch(setGroupItem(name));
  };

  const onSubmitValue = async (value: string, id: number, propertyValue: string) => {
    const propertyId = itemId?.properties?.find(
      (element: any) => element.name === propertyValue,
    )?.id;

    if (propertyId) {
      updateValue({
        item_id: id,
        request_body: {
          property_id: propertyId,
          value,
          scenario_id: baseScenario?.id,
        },
      });
    }
  };

  const addGroup = async (event: any) => {
    event.preventDefault();
    await dispatch(
      AddDimnesionGroupItem({
        dimension_id: currentDriver?.connected_dimension,
        request_body: {
          name: addGroupName,
          properties: [
            {
              property_id: currentDriver?.group_property_id,
              value: addGroupName,
            },
          ],
          scenario_id: baseScenario?.id,
        },
        toaster: {
          successMessage: 'Successfully Added',
          errorMessage: 'An item with this name already exists',
        },
      }),
    );
    await dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));
    refetchRequest();
  };

  const renameItemOnBlur = async () => {
    if (isItemRenamed) {
      await dispatch(
        UpdateItemName({
          item_id: renameItemName,
          request_body: { name: newItemName, scenario_id: baseScenario?.id },
        }),
      );
      dispatch(setIsItemRenamed(false));
      refetchRequest();
    }
  };

  useOutsideClick({
    ref: reference,
    handler: () => setDisplayGroupInfo(null),
  });

  const closeSection = () => {
    dispatch(closeInputSection());
    dispatch(setDriver(null));
  };

  const toggleInfoPopup = (id: number | null) => {
    if (displayGroupInfo === id) {
      return setDisplayGroupInfo(null);
    }
    return setDisplayGroupInfo(id);
  };

  const renameItem = (event: any, id: number) => {
    event.stopPropagation();
    setDisplayGroupInfo(null);
    if (renameGroup === id) {
      return setRenameGroup(0);
    }
    return setRenameGroup(id);
  };

  const DeleteDimItem = async (id: Number, type: string) => {
    if (type === 'dimension') {
      await dispatch(DeleteGroupDimName(id));
      await dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));
      await setIsDeleteModal(false);
      await setDeleteIdModal(null);
      await setDeleteTypeModal('');
      await setDeleteNameModal('');
    }
    if (type === 'item') {
      await dispatch(
        DeleteGroupItemName({ ItemId: id, parameters: { scenario_id: baseScenario?.id } }),
      );
      await dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));
      await setIsDeleteModal(false);
      await setDeleteIdModal(null);
      await setDeleteTypeModal('');
      await setDeleteNameModal('');
    }
  };

  const UpdateItemApi = async (item_id: Number, type: string) => {
    const requestBody = { name: renameGroupValue, scenario_id: baseScenario?.id };
    if (type === 'item') {
      await dispatch(
        UpdateGroupItemName({
          item_id,
          requestBody,
          toaster: {
            successMessage: 'Successfully Updated',
            errorMessage: 'An item with this name already exists',
          },
        }),
      );
      await dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));

      await setRenameGroup(null);
    }
    if (type === 'dimension') {
      await dispatch(
        UpdateGroupDimName({
          item_id,
          requestBody,
          toaster: {
            successMessage: 'Successfully Updated',
            errorMessage: 'An item with this name already exists',
          },
        }),
      );
      await dispatch(
        fetchBlockDrivers({
          blockId,
          parameters: {
            scenario_id: baseScenario?.id,
          },
        }),
      );
      await dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));

      setRenameGroup(null);
    }
  };

  const onKeyPressAction = async (event: any, item_id: number, type: string) => {
    if (event.code === 'Enter') {
      await setRenameGroup(item_id);
      await UpdateItemApi(item_id, type);
    }
    if (event.code === 'Escape') await setRenameGroup(0);
  };

  const onOpenDeleteModal = (event: any, name: any, id: any, type: any) => {
    event.stopPropagation();
    setIsDeleteModal(true);
    setDeleteIdModal(id);
    setDeleteTypeModal(type);
    setDeleteNameModal(name);
  };

  const onDeleteModel = () => {
    DeleteDimItem(DeleteIdModal, DeleteTypeModal);
  };

  const handleIssueDrop = async (properties: any) => {
    const droppableId = JSON.parse(properties?.destination.droppableId);
    const draggableId = JSON.parse(properties?.draggableId);
    setLoader(true);
    await dispatch(
      UpdateDimensionProperty({
        item_id: draggableId?.item_id,
        request_body: {
          property_id: draggableId?.property_id,
          value: droppableId?.name,
        },
      }),
    ).then(async () => {
      await dispatch(fetchDimensionItemsGroup({ currentDriver, scenario_id: baseScenario?.id }));
      setLoader(false);
    });
  };

  if (isLoading === 'IDLE') {
    return <FullScreenLoader bgColor='#796CD6' height={`calc(100vh - ${HEADER_HEIGHT} - 98px)`} />;
  }

  return (
    <VStack
      style={{
        width: '100%',
        justifyContent: 'flex-start',
        alignItems: 'center',
        padding: '140px 14px 0 14px',
        position: 'relative',
        height: `calc(100vh - 64px - 98px)`,
      }}
      onClick={() => {
        setDisplayInfo(null);
        dispatch(setRenameItemName(0));
        renameItemOnBlur();
      }}
    >
      {currentDriver?.group_property_id && currentDriver?.group_property && (
        <NewGroup
          {...{
            addGroupName,
            setAddGroupName,
            addGroup,
          }}
        />
      )}

      {!orgType && (
        <Cross
          styles={{ position: 'absolute', top: '10px', right: '10px' }}
          height={'25px'}
          width={'25px'}
          onClick={closeSection}
        />
      )}
      <DragDropContext onDragEnd={handleIssueDrop}>
        <ScrollContainer
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'flex-start',
            alignItems: 'center',
            minHeight: '500px',
            position: 'relative',
            height: '100%',
            flexDirection: 'row',
            marginBottom: '1rem',
          }}
          hideScrollbars={false}
          ignoreElements='div'
          className='horizontal_scroll'
        >
          {loader ? (
            <>
              {dataIndicatorsKeys &&
                dataIndicatorsKeys?.map((data: any) => {
                  return (
                    <Flex
                      key={data?.dim_id}
                      className='dimension'
                      m='0 14px'
                      flexDirection='column'
                      minW='310px'
                      alignSelf='flex-start'
                      alignItems='flex-start'
                      backgroundColor='rgba(67, 64, 111, 0.5)'
                      borderRadius='18px'
                      mb='15px'
                      padding='5px 10px 0 10px'
                      maxWidth={{ base: '400px', md: '400px' }}
                    >
                      <Box width={'100%'} pt={1} paddingBottom='1.5rem'>
                        <Skeleton height='20px' width='100%' mb={'1.5rem'} />
                        <Skeleton height='62px' width='100%' mb={'10px'} />
                        <Skeleton height='20px' width='100%' />
                      </Box>
                    </Flex>
                  );
                })}
            </>
          ) : (
            <>
              {dataIndicatorsKeys &&
                dataIndicatorsKeys?.map((dataKey: any, index: number) => {
                  const droppableId: string = JSON.stringify({
                    id: dataKey?.dim_id,
                    name: dataKey?.name,
                  });
                  return (
                    <React.Fragment key={index}>
                      <Droppable droppableId={droppableId} key={dataKey?.dim_id}>
                        {(provided: any) => {
                          return (
                            <Flex
                              ref={provided.innerRef}
                              className='dimension'
                              key={index}
                              zIndex={
                                displayGroupInfo === dataKey?.dim_id || selectedElement === index
                                  ? 2
                                  : 1
                              }
                              m='0 14px'
                              flexDirection='column'
                              minW='310px'
                              alignSelf='flex-start'
                              alignItems='flex-start'
                              backgroundColor='rgba(67, 64, 111, 0.5)'
                              borderRadius='18px'
                              mb='15px'
                              padding='5px 10px 0 10px'
                              maxWidth={{ base: '400px', md: '400px' }}
                            >
                              <Flex
                                justifyContent='space-between'
                                alignItems='center'
                                width={'100%'}
                                pt={1}
                                paddingBottom='1.5rem'
                              >
                                <Flex
                                  justifyContent='space-between'
                                  w='100%'
                                  alignItems='center'
                                  {...longClickCheck()}
                                  onDoubleClick={(event) => {
                                    setRenameGroup(dataKey?.dim_id);
                                    event.stopPropagation();
                                  }}
                                >
                                  {renameGroup === dataKey?.dim_id ? (
                                    <InputField
                                      width={'fit-content'}
                                      outline={'none !important'}
                                      boxShadow={'none !important'}
                                      border={'1px white solid !important'}
                                      padding={'15px 5px 15px 10px'}
                                      height={'28px'}
                                      fontSize={'16px !important'}
                                      fontFamily={'Roboto Bold'}
                                      defaultValue={dataKey?.name}
                                      clickedOutside={() => {
                                        setRenameGroup(0);
                                      }}
                                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        setRenameGroupValue(event.target.value);
                                      }}
                                      onKeyDown={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        onKeyPressAction(event, dataKey?.dim_id, dataKey?.type);
                                      }}
                                      _placeholder={{ color: '#ffffff66' }}
                                      autoFocus
                                    />
                                  ) : (
                                    <Tooltip label={dataKey?.name}>
                                      <Text
                                        fontFamily='Roboto Regular'
                                        fontSize='22px'
                                        lineHeight='9'
                                        width={'90%'}
                                        maxW={'100%'}
                                        cursor='pointer'
                                        overflow={'hidden'}
                                        textOverflow={'ellipsis'}
                                        noOfLines={isLongClicked ? 0 : 1}
                                      >
                                        {dataKey?.name}
                                      </Text>
                                    </Tooltip>
                                  )}
                                </Flex>
                                <Flex alignItems='center' position='relative' ref={reference}>
                                  <Menu isOpen={displayGroupInfo === dataKey?.dim_id}>
                                    <MenuButton
                                      width={'auto'}
                                      marginRight='5px'
                                      cursor={planPermission ? 'not-allowed' : 'pointer'}
                                      onClick={
                                        planPermission
                                          ? undefined
                                          : (event) => {
                                              event.stopPropagation();
                                              toggleInfoPopup(dataKey?.dim_id);
                                            }
                                      }
                                    >
                                      <Image
                                        src={infoIcon}
                                        w='28px'
                                        height='8px'
                                        className='infoButton'
                                      />
                                    </MenuButton>
                                    <MenuList>
                                      <MenuItem
                                        onClick={(event) => renameItem(event, dataKey?.dim_id)}
                                        padding='.5rem 1.5rem'
                                      >
                                        Rename
                                      </MenuItem>
                                      <MenuItem
                                        onClick={(event) => {
                                          onOpenDeleteModal(
                                            event,
                                            dataKey?.name,
                                            dataKey?.dim_id,
                                            dataKey?.type,
                                          );
                                        }}
                                        padding='.5rem 1.5rem'
                                      >
                                        Delete
                                      </MenuItem>
                                    </MenuList>
                                  </Menu>
                                </Flex>
                              </Flex>

                              <DesktopElements
                                {...{
                                  _index: index,
                                  dataKey,
                                  styles,
                                  onSubmitValue,
                                  longClickCheck,
                                  refetchRequest,
                                  isLongClicked,
                                  planPermission,
                                }}
                              />

                              <AddNewItem
                                {...{
                                  dataKey,
                                  styles,
                                  planPermission,
                                  refetchRequest,
                                }}
                              />

                              <Flex
                                justifyContent='center'
                                alignItems='center'
                                pb='15px'
                                cursor={planPermission ? 'not-allowed' : 'pointer'}
                                onClick={
                                  planPermission ? undefined : () => handleItem(dataKey?.name)
                                }
                              >
                                <Box mr='15px'>
                                  <Plus height={5} width={5} />
                                </Box>
                                <Tooltip label={currentDriver?.dim_name}>
                                  <Text fontSize='1xl' pt='2px'>
                                    {currentDriver?.dim_name}
                                  </Text>
                                </Tooltip>
                              </Flex>
                            </Flex>
                          );
                        }}
                      </Droppable>
                    </React.Fragment>
                  );
                })}
            </>
          )}
        </ScrollContainer>
      </DragDropContext>
      <DeleteGroupModal
        isOpen={isDeleteModal}
        onClose={() => setIsDeleteModal(false)}
        itemName={DeleteNameModal}
        onDelete={onDeleteModel}
      />
    </VStack>
  );
};

export default PlanPermission(DesktopPanel);
